import Clarity from '@microsoft/clarity'

const config = {
  projectId: 'f4ynid6270',
  stagingProjectId: 'obvfxdsh5x',
  enableInProduction: true,
  enableInStaging: true,
  enableInDevelopment: false,
  excludeAdminPages: true
}

let isInitialized = false

const getProjectId = () => {
  const mode = import.meta.env?.MODE || 'development'
  return mode === 'staging' ? config.stagingProjectId : config.projectId
}

const shouldInitialize = () => {
  const mode = import.meta.env?.MODE || 'development'

  if (config.excludeAdminPages && window.location.pathname.startsWith('/admin')) {
    return false
  }

  switch (mode) {
    case 'production':
      return config.enableInProduction
    case 'staging':
      return config.enableInStaging
    case 'development':
      return config.enableInDevelopment
    default:
      return false
  }
}

const areStylesFullyLoaded = () => {
  // 1. Document'ın tam yüklendiğinden emin ol
  if (document.readyState !== 'complete') {
    return false
  }

  const rootStyles = getComputedStyle(document.documentElement)

  // 2. app.scss'den gelen --cssLoadedFlag CSS variable'ını kontrol et
  const cssLoadedFlag = rootStyles.getPropertyValue('--cssLoadedFlag').trim()
  if (cssLoadedFlag !== '1') {
    return false
  }

  // 3. style.scss'den gelen --styleCssLoadedFlag CSS variable'ını kontrol et
  const styleCssLoadedFlag = rootStyles.getPropertyValue('--styleCssLoadedFlag').trim()
  if (styleCssLoadedFlag !== '2') {
    return false
  }

  // 4. Bootstrap class testi - d-none class'ının çalıştığını doğrula
  const testElement = document.createElement('div')
  testElement.className = 'd-none'
  testElement.style.position = 'absolute'
  testElement.style.visibility = 'hidden'
  document.body.appendChild(testElement)

  const computedStyle = window.getComputedStyle(testElement)
  const hasBootstrapStyles = computedStyle.display === 'none'

  document.body.removeChild(testElement)

  return hasBootstrapStyles
}

const initializeWhenStylesReady = () => {
  if (isInitialized) return

  let attempts = 0
  const maxAttempts = 600 // ~10 saniye (60fps * 10)
  const minWaitAttempts = 60 // En az 1 saniye bekle

  const checkAndInit = () => {
    attempts++

    const stylesLoaded = areStylesFullyLoaded()

    // En az minWaitAttempts kadar bekle, sonra CSS kontrolü yap
    if (attempts >= minWaitAttempts && stylesLoaded) {
      // CSS hazır, 3 frame daha bekle (paint ve layout tamamlansın)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            doInitialize()
          })
        })
      })
    } else if (attempts < maxAttempts) {
      requestAnimationFrame(checkAndInit)
    } else {
      const rootStyles = getComputedStyle(document.documentElement)
      const cssFlag = rootStyles.getPropertyValue('--cssLoadedFlag').trim()
      const styleFlag = rootStyles.getPropertyValue('--styleCssLoadedFlag').trim()

      console.error('[Clarity] CSS load check FAILED after', attempts, 'attempts.')
      console.error(
        '[Clarity] --cssLoadedFlag:',
        cssFlag || '(empty)',
        '| --styleCssLoadedFlag:',
        styleFlag || '(empty)'
      )
      console.error('[Clarity] NOT initializing to prevent raw HTML recording.')
      // Raw HTML kaydetmemek için Clarity'yi başlatMIYORUZ
    }
  }

  // İlk frame'de kontrol başlat
  requestAnimationFrame(checkAndInit)
}

const doInitialize = () => {
  if (isInitialized) return

  try {
    Clarity.init(getProjectId())
    isInitialized = true

    const mode = import.meta.env?.MODE || 'development'
    const country = import.meta.env?.VITE_COUNTRY || 'unknown'
    Clarity.setTag('environment', mode)
    Clarity.setTag('country', country)

    console.log('[Clarity] Successfully initialized after CSS loaded.')
  } catch (error) {
    console.error('[Clarity] Failed to initialize:', error)
  }
}

export const clarityService = {
  initialize() {
    if (isInitialized) {
      return
    }

    if (!shouldInitialize()) {
      return
    }

    initializeWhenStylesReady()
  },

  identify(userId) {
    if (!isInitialized) return
    try {
      Clarity.identify(userId)
    } catch (error) {
      console.error('[Clarity] Failed to identify user:', error)
    }
  },

  setTag(key, value) {
    if (!isInitialized) return
    try {
      Clarity.setTag(key, value)
    } catch (error) {
      // silently fail
    }
  },

  event(eventName) {
    if (!isInitialized) return
    try {
      Clarity.event(eventName)
    } catch (error) {
      // silently fail
    }
  },

  get initialized() {
    return isInitialized
  }
}
